import { Box, TextField, CircularProgress } from "@mui/material";
import MDTypography from "components/MDTypography";
import { useState, useCallback, useMemo, memo } from "react";

interface InlineEditableFieldProps {
  value: string;
  onSave: (value: string) => Promise<void>;
  formatDisplay?: (value: string) => string;
  parseInput?: (value: string) => string;
  type?: "text" | "currency" | "percentage";
  disabled?: boolean;
  fontSize?: string;
  minWidth?: string;
  validate?: (value: string) => boolean;
}

function InlineEditableField({
  value,
  onSave,
  formatDisplay,
  parseInput,
  type = "text",
  disabled = false,
  fontSize = "1rem",
  minWidth = "80px",
  validate,
}: InlineEditableFieldProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [isSaving, setIsSaving] = useState(false);
  const [lastPropValue, setLastPropValue] = useState(value);
  const [hasError, setHasError] = useState(false);

  const inputRef = useCallback((node: HTMLInputElement | null) => {
    if (node) {
      node.focus();
      node.select();
    }
  }, []);

  if (value !== lastPropValue) {
    setLastPropValue(value);
    if (!isEditing) {
      setEditValue(value);
    }
  }

  const isValid = useMemo(() => {
    if (!validate) return true;
    return validate(editValue);
  }, [validate, editValue]);

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!disabled && !isSaving) {
        setEditValue(value);
        setIsEditing(true);
      }
    },
    [disabled, isSaving, value]
  );

  const handleSave = useCallback(async () => {
    if (!isValid) return;
    const finalValue = parseInput ? parseInput(editValue) : editValue;
    setIsEditing(false);
    if (finalValue !== value) {
      setIsSaving(true);
      try {
        await onSave(finalValue);
      } finally {
        setIsSaving(false);
      }
    }
  }, [editValue, value, onSave, parseInput, isValid]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      e.stopPropagation();
      if (e.key === "Enter") {
        e.preventDefault();
        if (isValid) {
          setHasError(false);
          handleSave();
        } else {
          setHasError(true);
        }
      } else if (e.key === "Escape") {
        setEditValue(value);
        setIsEditing(false);
      }
    },
    [handleSave, value, isValid]
  );

  const handleBlur = useCallback(() => {
    if (isValid) {
      setHasError(false);
      handleSave();
    }
  }, [handleSave, isValid]);

  const displayValue = useMemo(() => {
    const rawValue = isEditing || isSaving ? editValue : value;
    if (!formatDisplay) return rawValue;
    if (rawValue.trim() === "") return "";
    return formatDisplay(rawValue);
  }, [formatDisplay, editValue, value, isEditing, isSaving]);

  if (isEditing) {
    return (
      <Box
        display="flex"
        alignItems="center"
        sx={{ minWidth }}
        onClick={(e) => e.stopPropagation()}
      >
        <TextField
          inputRef={inputRef}
          value={editValue}
          onChange={(e) => {
            const nextValue = e.target.value;
            if (hasError && (!validate || validate(nextValue))) {
              setHasError(false);
            }
            setEditValue(nextValue);
          }}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          size="small"
          variant="standard"
          error={hasError && !isValid}
          type="text"
          InputProps={{
            sx: {
              fontSize,
              padding: 0,
              backgroundColor: hasError && !isValid ? "rgba(211, 47, 47, 0.1)" : "transparent",
              borderRadius: "4px",
              px: hasError && !isValid ? 0.5 : 0,
            },
          }}
          sx={{
            minWidth,
            "& .MuiInput-underline:before": {
              borderBottomColor: hasError && !isValid ? "error.main" : "primary.main",
            },
            "& .MuiInput-underline:after": {
              borderBottomColor: hasError && !isValid ? "error.main" : "primary.main",
            },
          }}
          inputProps={{
            inputMode: type === "currency" || type === "percentage" ? "decimal" : undefined,
            pattern: type === "currency" || type === "percentage" ? "[0-9]*[.]?[0-9]*" : undefined,
          }}
        />
        {isSaving && <CircularProgress size={14} sx={{ ml: 1 }} />}
      </Box>
    );
  }

  return (
    <Box
      onClick={handleClick}
      sx={{
        cursor: disabled ? "default" : "pointer",
        minWidth,
        display: "inline-flex",
        alignItems: "center",
        "&:hover": disabled
          ? {}
          : {
              "& .editable-value": {
                borderBottomColor: "primary.main",
              },
            },
      }}
    >
      <MDTypography
        variant="body"
        className="editable-value"
        sx={{
          fontSize,
          borderBottom: disabled ? "none" : "1px solid #1976d2",
          transition: "border-color 0.2s",
          lineHeight: 1.4,
        }}
      >
        {displayValue}
      </MDTypography>
      {isSaving && <CircularProgress size={14} sx={{ ml: 1 }} />}
    </Box>
  );
}

export default memo(InlineEditableField);
